import { useCallback, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ScrollingTextArea } from "@bagel-web/components";

import {
  CommonLimitedListState,
  CommonPuppeteerLimitedListState,
} from "../../../types";
import {
  ASHComponentVisualization,
  ASHComponentVisualizationProps,
} from "../ASHComponentCard";
import { applyItemEdit } from "../../../playground/playgroundComponentState";
import { useAgentViewPreferences } from "../../viewPreferences/agentViewPreferences";
import { ItemView } from "./LimitedListCells";
import LimitedListDragOverlay from "./LimitedListDragOverlay";
import LimitedListTable from "./LimitedListTable";
import LimitedListToolbar from "./LimitedListToolbar";
import {
  itemContentText,
  LimitedListItemEdit,
  orderAfterMove,
} from "./limitedListItems";
import { blankSeedMessage, LimitedListMessageType } from "./limitedListSeed";
import useItemEditing from "./useItemEditing";

const LimitedListVisualization: ASHComponentVisualization<
  CommonLimitedListState | CommonPuppeteerLimitedListState
> = ({
  componentState,
  sessionId,
  agentId,
  agencyTagFilter,
  isEditMode,
  messages = [],
  onMessagesChange,
}: ASHComponentVisualizationProps<
  CommonLimitedListState | CommonPuppeteerLimitedListState
>) => {
  const {
    getComponentTextFilter,
    showNewestMessagesFirst,
    showCompactStateContent,
  } = useAgentViewPreferences();
  const textFilter = getComponentTextFilter(componentState?.id);
  const data = componentState?.state?.data ?? [];

  // Items and messages are index-aligned — the seed presents each message
  // as one item — so an item edit lands on `messages[index]`. Every edit
  // hands back the component's whole message list.
  const editItem = useCallback(
    (index: number, edit: LimitedListItemEdit) =>
      onMessagesChange?.(
        messages.map((message, candidate) =>
          candidate === index ? applyItemEdit(message, edit) : message
        )
      ),
    [messages, onMessagesChange]
  );
  const deleteItem = (index: number) =>
    onMessagesChange?.(messages.filter((_, candidate) => candidate !== index));
  // Appended last: the most-recent position in the component's sequence.
  const addItem = (messageType: LimitedListMessageType) =>
    onMessagesChange?.([...messages, blankSeedMessage(messageType)]);
  // `order` is the full new sequence, as previous item indices.
  const reorderItems = (order: Array<number>) =>
    onMessagesChange?.(
      order.flatMap((previous) => {
        const message = messages[previous];
        return message ? [message] : [];
      })
    );

  const {
    editing,
    inputRef,
    startEditing,
    changeEditing,
    cancelEditing,
    applyEditing,
  } = useItemEditing(data, editItem);

  // Rows keep their data position through filtering and the ordering
  // toggle: `index` is the sortable id and what the edit callbacks report.
  const visibleRows = data
    .map((item, index) => ({ item, index, contentText: itemContentText(item) }))
    .filter(
      (row) =>
        row.contentText !== null &&
        agencyTagFilter.matchAny(row.item.agency_tags) &&
        (textFilter.length === 0 ||
          row.contentText.toLowerCase().includes(textFilter.toLowerCase()))
    );
  const displayRows = showNewestMessagesFirst
    ? visibleRows.toReversed()
    : visibleRows;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // The dragged row renders again in the overlay; while it is set, the
  // source row stays put in the table.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragItem = dragIndex === null ? null : (data[dragIndex] ?? null);

  const handleDragStart = (event: DragStartEvent) => {
    // An edit left open on another row (invalid JSON) dies here, as on
    // delete: the reorder would move a different item under its index.
    cancelEditing();
    setDragIndex(typeof event.active.id === "number" ? event.active.id : null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDragIndex(null);
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const from = displayRows.findIndex((row) => row.index === active.id);
    const to = displayRows.findIndex((row) => row.index === over.id);
    if (from < 0 || to < 0) {
      return;
    }
    reorderItems(
      orderAfterMove(
        data.length,
        visibleRows.map((row) => row.index),
        showNewestMessagesFirst,
        from,
        to
      )
    );
  };

  const view: ItemView = {
    sessionId,
    agentId,
    isEditMode,
    textFilter,
    compact: showCompactStateContent,
  };
  const table = (
    <LimitedListTable
      rows={displayRows}
      view={view}
      editing={
        editing && {
          index: editing.index,
          editor: {
            text: editing.text,
            isInvalid: editing.isInvalid,
            inputRef,
            onChange: changeEditing,
            onApply: applyEditing,
          },
        }
      }
      onStartEdit={startEditing}
      onDelete={(index) => {
        // An edit left open on another row (invalid JSON) dies here — the
        // deletion shifts the indices under it.
        cancelEditing();
        deleteItem(index);
      }}
    />
  );

  return (
    <>
      <LimitedListToolbar
        componentId={componentState?.id}
        isEditMode={isEditMode}
        onAdd={addItem}
      />
      <ScrollingTextArea className={"font-monospace"} $height="1000px">
        {isEditMode ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragCancel={() => setDragIndex(null)}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={displayRows.map((row) => row.index)}
              strategy={verticalListSortingStrategy}
            >
              {table}
            </SortableContext>
            <LimitedListDragOverlay item={dragItem} view={view} />
          </DndContext>
        ) : (
          table
        )}
      </ScrollingTextArea>
    </>
  );
};

export default LimitedListVisualization;
